import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActiveCompanyService } from './active-company.service';
import { AuthService } from './auth.service';
import { CompanyService } from './company.service';
import type { AppModuleKey } from '../constants/app-modules';

/**
 * Client-side module visibility for UX only. Backend enforces access on every API call.
 */
@Injectable({ providedIn: 'root' })
export class ModuleAccessService {
  private readonly activeCompany = inject(ActiveCompanyService);
  private readonly auth = inject(AuthService);
  private readonly companyService = inject(CompanyService);

  /** null = not yet loaded or load failed (permissive hasModule); otherwise explicit allow-list from context API. */
  private readonly enabledKeys = signal<ReadonlySet<AppModuleKey> | null>(null);
  /** Membership / ownership role from last company context load (`owner`, `admin`, …). */
  private readonly activeCompanyRoleInternal = signal<string | null>(null);
  readonly activeCompanyRole = this.activeCompanyRoleInternal.asReadonly();

  /**
   * Increments after each module context resolution. OnPush views can depend on this
   * (e.g. `moduleUiVersion()`) so `hasModule()` results refresh after async load.
   */
  private readonly moduleUiVersionInternal = signal(0);
  readonly moduleUiVersion = this.moduleUiVersionInternal.asReadonly();

  private inflight: Promise<void> | null = null;

  constructor() {
    const destroyRef = inject(DestroyRef);
    this.activeCompany.companyChanged$
      .pipe(takeUntilDestroyed(destroyRef))
      .subscribe(() => {
        void this.refreshFromActiveCompany();
      });
  }

  /** Effective enabled keys for the active company (empty set if none). Super-admin: full catalog. */
  enabledModuleKeys(): ReadonlySet<AppModuleKey> | null {
    return this.enabledKeys();
  }

  /**
   * Super-admin: all modules for UX.
   * null set: permissive (still loading or error — avoid locking users out of navigation).
   * Otherwise: key must be in the server-provided list.
   */
  hasModule(key: AppModuleKey): boolean {
    if (this.auth.isSuperAdmin()) {
      return true;
    }
    const set = this.enabledKeys();
    if (set === null) {
      return true;
    }
    return set.has(key);
  }

  hasAllModules(keys: readonly AppModuleKey[]): boolean {
    return keys.every((k) => this.hasModule(k));
  }

  /** True when active company context role is owner (or super-admin). */
  isActiveCompanyOwner(): boolean {
    if (this.auth.isSuperAdmin()) {
      return true;
    }
    return String(this.activeCompanyRoleInternal() ?? '').toLowerCase() === 'owner';
  }

  /** Wait for a context fetch for the current active company (deduped). Use in route guards. */
  async ensureReady(): Promise<void> {
    if (this.auth.isSuperAdmin()) {
      return;
    }
    const id = this.activeCompany.getActiveOrFallbackCompanyId();
    if (!id) {
      return;
    }
    if (this.inflight) {
      await this.inflight;
      return;
    }
    this.inflight = this.loadForCompanyId(id).finally(() => {
      this.inflight = null;
    });
    await this.inflight;
  }

  /** Load modules for whichever company is active (or fallback). */
  async refreshFromActiveCompany(): Promise<void> {
    const id = this.activeCompany.getActiveOrFallbackCompanyId();
    await this.loadForCompanyId(id);
  }

  private async loadForCompanyId(companyId: string | null): Promise<void> {
    try {
      if (this.auth.isSuperAdmin()) {
        this.enabledKeys.set(null);
        return;
      }
      if (!companyId) {
        this.enabledKeys.set(null);
        return;
      }
      try {
        const ctx = await this.companyService.getCompanyContext(companyId);
        const list = (ctx.enabledModules || []) as AppModuleKey[];
        this.enabledKeys.set(new Set(list));
        this.activeCompanyRoleInternal.set(ctx.role ? String(ctx.role) : null);
        // Keep profile.companyRole in sync for guards that read AuthService.isCompanyOwner()
        const profile = this.auth.currentUserProfile();
        if (profile && ctx.role) {
          (profile as { companyRole?: string }).companyRole = String(ctx.role);
        }
      } catch {
        this.enabledKeys.set(null);
        this.activeCompanyRoleInternal.set(null);
      }
    } finally {
      this.moduleUiVersionInternal.update((v) => v + 1);
    }
  }

  /** Clear module permissions on logout or user switch. */
  resetSessionState(): void {
    this.enabledKeys.set(null);
    this.activeCompanyRoleInternal.set(null);
    this.inflight = null;
    this.moduleUiVersionInternal.update((v) => v + 1);
  }
}
